"use client";

import { generatePKCEPair } from "@nhost/nhost-js/auth";
import { usePostHog } from "posthog-js/react";
import { useCallback, useRef, useState } from "react";
import { useRouteLanguage } from "@/lib/locale/navigation";
import { localizeHref } from "@/lib/locale/paths";
import { useNhost } from "@/lib/nhost/react";
import { resolveSafeToPath } from "./destination";
import {
  clearPendingGoogleOAuth,
  GOOGLE_OAUTH_CALLBACK_PATH,
  type GoogleOAuthEntryPoint,
  type GoogleOAuthFailureReason,
  getGoogleOAuthSessionStorage,
  savePendingGoogleOAuth,
  toGoogleOAuthAnalyticsEntryPoint,
} from "./google-oauth";

type UseGoogleOAuthOptions = {
  entryPoint: GoogleOAuthEntryPoint;
  locale?: string;
  prepareAttempt: () => { destination: string; metadata: Record<string, unknown> };
  onStart?: () => void;
  onFailure: (reason: GoogleOAuthFailureReason) => void;
};

export const useGoogleOAuth = ({
  entryPoint,
  locale,
  prepareAttempt,
  onStart,
  onFailure,
}: UseGoogleOAuthOptions) => {
  const routeLanguage = useRouteLanguage();
  const nhost = useNhost();
  const posthog = usePostHog();
  const isStartingRef = useRef(false);
  const [isStarting, setIsStarting] = useState(false);

  const startGoogleOAuth = useCallback(async () => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;
    setIsStarting(true);
    onStart?.();

    let failureReason: GoogleOAuthFailureReason = "local_flow_invalid";
    const storage = getGoogleOAuthSessionStorage();
    try {
      const attempt = prepareAttempt();
      const safeDestination = resolveSafeToPath({
        defaultPath: "/app/dashboard/",
        toPathParam: attempt.destination,
      });
      const flowId = crypto.randomUUID();
      const { verifier, challenge } = await generatePKCEPair();
      const saved = savePendingGoogleOAuth(storage, {
        flowId,
        codeVerifier: verifier,
        destination: localizeHref(safeDestination, routeLanguage),
        entryPoint,
        createdAtMs: Date.now(),
      });
      if (!saved) {
        failureReason = "storage_unavailable";
        throw new Error("OAuth session storage is unavailable");
      }

      const callbackUrl = new URL(
        localizeHref(GOOGLE_OAUTH_CALLBACK_PATH, routeLanguage),
        window.location.origin
      ).href;
      const providerUrl = nhost.auth.signInProviderURL("google", {
        codeChallenge: challenge,
        ...(locale ? { locale } : {}),
        metadata: attempt.metadata,
        redirectTo: callbackUrl,
        state: flowId,
      });
      window.location.assign(providerUrl);
    } catch {
      clearPendingGoogleOAuth(storage);
      posthog?.capture("auth_oauth_failed", {
        entry_point: toGoogleOAuthAnalyticsEntryPoint(entryPoint),
        reason: failureReason,
      });
      onFailure(failureReason);
      isStartingRef.current = false;
      setIsStarting(false);
    }
  }, [routeLanguage, entryPoint, locale, nhost, onFailure, onStart, posthog, prepareAttempt]);

  return { isStarting, startGoogleOAuth };
};
